import { supabase } from "@/lib/supabase";
import { readAnonId, resetConsent, setConsent } from "@/lib/anonId";
import { setAutoVisits } from "@/lib/autoVisits";

/**
 * Erasing what Resto keeps about this device: the consent withdrawal behind
 * Settings → "Forget this device", and the local clean-up after an account
 * deletion. "Forget" is all-or-nothing (a failed server erase changes nothing
 * locally); the post-deletion clean-up is best effort and always clears.
 */

/** Personal on-device keys besides consent / anon id / auto-visits. */
const PERSONAL_KEYS = ["resto_tap_history", "resto_feedback_prompt", "resto_pending_invite"] as const;

function removePersonalKeys(): void {
  try {
    for (const k of PERSONAL_KEYS) window.localStorage.removeItem(k);
  } catch {
    // Storage unavailable — nothing was stored either.
  }
}

/**
 * Erase the guest-era rows (visits, feedback, events) recorded under this
 * device's anon id, server-side (schema §31d `forget_device`). True when there
 * was nothing to erase or the erase succeeded; false on any failure, so the
 * caller can keep the id (the only key that reaches those rows) and retry.
 */
async function eraseGuestRows(anon: string | null): Promise<boolean> {
  if (!anon) return true;
  try {
    const { error } = await supabase.rpc("forget_device", { p_anon: anon });
    if (error) console.warn("[deviceData] forget_device failed:", error.message);
    return !error;
  } catch {
    return false; // offline
  }
}

/**
 * Withdraw consent and forget this device. The server-side erase runs first;
 * only when it succeeds do the id, the tap history and the auto-visits opt-in
 * go from this browser (consent stored as "denied" so the notice doesn't
 * re-ask; Settings can allow it again). On failure nothing changes locally and
 * this returns false, so the UI never claims an erase that didn't happen.
 */
export async function forgetThisDevice(): Promise<boolean> {
  if (!(await eraseGuestRows(readAnonId()))) return false;
  setConsent(false);
  setAutoVisits(false);
  removePersonalKeys();
  return true;
}

/**
 * After `delete_own_account`: erase the guest-era rows under this device's id
 * too (best effort — §31b already covers ids the account linked; this catches
 * one that never got linked), then clear the device.
 */
export async function clearDeviceAfterAccountDeletion(): Promise<void> {
  await eraseGuestRows(readAnonId());
  resetConsent();
  setAutoVisits(false);
  removePersonalKeys();
}
